import { useUpdateSsoConnectionEnforcement } from '@tinct/core-api';
import {
  Alert,
  AlertDescription,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@tinct/web-components';
import { TriangleAlertIcon } from 'lucide-react';
import { toast } from 'sonner';

type SsoEnforcementModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
  /** The state being confirmed: `true` requires single sign-on, `false` lifts the requirement. */
  enforced: boolean;
  /**
   * Active members whose address is on a verified domain — the people the identity provider takes
   * over. `null` when the viewer cannot count them (see `countMembersOnVerifiedDomains`).
   */
  membersOnVerifiedDomainsCount?: number | null;
  /** The member list was longer than what was counted. */
  membersCountIsPartial?: boolean;
  /**
   * Who is confirming. The back office acts on a workspace it is not a member of, so the wording
   * speaks of "this workspace" rather than "yours" and recalls the identity check support owes the
   * requester. Only the lifting wording differs: staff can never require single sign-on (403).
   */
  surface?: 'workspace' | 'back-office';
};

function countedMembers(count: number, isPartial: boolean) {
  return `${isPartial ? 'at least ' : ''}${count} active ${count === 1 ? 'member' : 'members'}`;
}

function describeEnforce(count: number | null | undefined, isPartial: boolean) {
  if (count == null) {
    return 'Everyone whose address is on a verified domain of this workspace will sign in through your identity provider, and only through it.';
  }
  return `${countedMembers(count, isPartial)} on your verified domains will sign in through your identity provider, and only through it.`;
}

function describeLift(
  surface: 'workspace' | 'back-office',
  count: number | null | undefined,
  isPartial: boolean,
) {
  if (surface === 'workspace') {
    return 'People on your verified domains will be able to sign in with a password or a social account again. Those signed in through your identity provider stay signed in.';
  }
  const people =
    count == null
      ? 'Everyone on this workspace\'s verified domains'
      : `${countedMembers(count, isPartial)} on this workspace's verified domains`;
  return `${people} will be able to sign in with a password or a social account again. Those signed in through the identity provider stay signed in.`;
}

/**
 * Confirms requiring single sign-on for the workspace's verified domains, or lifting the
 * requirement. Surface-agnostic (no workspace hooks): used by the workspace security page and by
 * the back office, where support can lift a requirement that locks a workspace out.
 */
export const SsoEnforcementModal = ({
  open,
  onOpenChange,
  organizationId,
  enforced,
  membersOnVerifiedDomainsCount,
  membersCountIsPartial = false,
  surface = 'workspace',
}: SsoEnforcementModalProps) => {
  const updateEnforcement = useUpdateSsoConnectionEnforcement({
    onSuccess: () => {
      toast.success(
        enforced
          ? 'Single sign-on is now required'
          : 'Single sign-on is no longer required',
      );
      onOpenChange(false);
    },
  });

  const handleConfirm = () => {
    updateEnforcement.mutate({ organizationId, enforced });
  };

  // The API is the gate, and its refusals are actionable: 409 (5508) asks the admin to sign in
  // through the identity provider first, 409 (5506) that the connection be enabled. The dialog
  // stays open with that message instead of closing on a toast the admin reads too late.
  const refusal = updateEnforcement.error?.error ?? null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {enforced
              ? 'Require single sign-on?'
              : 'Stop requiring single sign-on?'}
          </DialogTitle>
          <DialogDescription>
            {enforced
              ? describeEnforce(
                  membersOnVerifiedDomainsCount,
                  membersCountIsPartial,
                )
              : describeLift(
                  surface,
                  membersOnVerifiedDomainsCount,
                  membersCountIsPartial,
                )}
          </DialogDescription>
        </DialogHeader>

        {enforced && (
          <div className="flex flex-col gap-2 text-sm text-muted-foreground">
            <span>
              Passwords, password resets, social sign-in and e-mail changes stop
              working for those addresses, and everyone currently signed in with
              one of them is signed out.
            </span>
            <span>
              Guests on other domains, service accounts and AI assistants are
              unaffected.
            </span>
          </div>
        )}

        {/* Lifting for a customer is a support act, not a setting: the workspace is handed back to
            passwords on someone's word, so the operator is reminded whose word it has to be. */}
        {!enforced && surface === 'back-office' && (
          <div className="flex flex-col gap-2 text-sm text-muted-foreground">
            <span>
              Lift the requirement only once you have verified the requester's
              identity through the support procedure — a request to hand a
              workspace back to passwords is exactly what an attacker locked out
              of it would ask for.
            </span>
            <span>
              The workspace's admins can require it again themselves, and the
              lift is recorded against your account.
            </span>
          </div>
        )}

        {refusal && (
          <Alert variant="important">
            <TriangleAlertIcon />
            <AlertDescription>{refusal}</AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant={enforced ? 'default' : 'destructive'}
            onClick={handleConfirm}
            isLoading={updateEnforcement.isPending}
          >
            {enforced ? 'Require SSO' : 'Stop requiring SSO'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
